import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Form, Select, Space, Spin } from 'antd';
import { useForm } from 'antd/lib/form/Form';
import { useEffect, useState } from 'react';
import { Area, Smat, User } from '../../models';
import { apiClient, apiErrorMessage } from '../../utils';
import { toLowerTr } from '../../utils';

export function SmatPlanEditPage(props: { userId?: string; onSaved: () => void }) {
  const [usersLoading, setUsersLoading] = useState(false);
  const [smatsLoading, setSmatsLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<User[]>();
  const [selectedUserId, setSelectedUserId] = useState<string>();
  const [areas, setAreas] = useState<Area[]>();
  const [areasLoading, setAreasLoading] = useState(false);
  const [plansFormValues, setPlansFormValues] = useState<any>();

  const [planForm] = useForm();

  const weeks = Array.from(Array(52).keys());

  useEffect(() => {
    loadUsers();
    loadAreas();
  }, []);

  useEffect(() => {
    if (props.userId) {
      setSelectedUserId(props.userId);
    }
  }, [props.userId]);

  const loadUsers = () => {
    setUsersLoading(true);
    apiClient
      .get('users')
      .then(response => {
        setUsers(response.data);
        setUsersLoading(false);
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
        setUsersLoading(false);
      });
  };

  const loadAreas = () => {
    setAreasLoading(true);
    apiClient
      .get('areas')
      .then(response => {
        setAreas(response.data);
        setAreasLoading(false);
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
        setAreasLoading(false);
      });
  };

  useEffect(() => {
    if (!selectedUserId) {
      setPlansFormValues(undefined);
      return;
    }
    setSmatsLoading(true);
    apiClient
      .get(`smats?year=2023&userId=${selectedUserId}&scope=plan`)
      .then(response => {
        const smats = response.data as Smat[];
        const plans = smats.map(p => {
          return { week: p.week, areaId: p.area.id };
        });
        setPlansFormValues({ plans });
        planForm.setFieldsValue({ plans });
        setSmatsLoading(false);
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
        setSmatsLoading(false);
      });
  }, [planForm, selectedUserId]);

  const onUserChange = (userId: string) => {
    setSelectedUserId(userId);
  };

  const onFinish = (values: any) => {
    const body = { ...values, userId: selectedUserId, year: 2023 };
    setLoading(true);
    apiClient
      .post('smats', body)
      .then(() => {
        props.onSaved();
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <>
      <Form labelCol={{ span: 6 }} wrapperCol={{ span: 18 }}>
        <Form.Item label="Personel">
          {users && (
            <Select
              value={selectedUserId}
              onChange={onUserChange}
              loading={usersLoading}
              showSearch
              filterOption={(input, option) => (toLowerTr(option?.children)?.indexOf(toLowerTr(input) || input) || -1) >= 0}
            >
              {users?.map(e => (
                <Select.Option key={e.id} value={e.id}>
                  {e.fullNameId}
                </Select.Option>
              ))}
            </Select>
          )}
        </Form.Item>
      </Form>
      {selectedUserId !== undefined && (
        <div>
          <Spin spinning={smatsLoading} style={{ width: '100%' }}>
            {plansFormValues && (
              <Form form={planForm} labelCol={{ span: 6 }} wrapperCol={{ span: 18 }} initialValues={plansFormValues} onFinish={onFinish}>
                <Form.List name="plans">
                  {(fields, { add, remove }, { errors }) => (
                    <>
                      {fields.map((field, index) => (
                        <Form.Item
                          labelCol={{ span: index === 0 ? 6 : 0 }}
                          wrapperCol={{ span: index === 0 ? 18 : 24, offset: index === 0 ? 0 : 6 }}
                          label={index === 0 ? 'Planlar' : ''}
                          required={false}
                          key={field.key}
                        >
                          <Space style={{ display: 'flex', width: '100%', marginBottom: 8 }}>
                            <Form.Item
                              {...field}
                              validateTrigger={['onChange', 'onBlur']}
                              rules={[
                                {
                                  required: true,
                                  message: 'Zorunlu alan',
                                },
                              ]}
                              name={[field.name, 'week']}
                              fieldKey={[field.fieldKey, 'week']}
                              key={field.key + '_week'}
                              noStyle
                            >
                              <Select style={{ width: '7rem' }}>
                                {weeks?.map(m => (
                                  <Select.Option key={field.key + '_' + (m + 1)} value={m + 1}>
                                    {m + 1}
                                  </Select.Option>
                                ))}
                              </Select>
                            </Form.Item>
                            <Form.Item
                              {...field}
                              validateTrigger={['onChange', 'onBlur']}
                              rules={[
                                {
                                  required: true,
                                  message: 'Zorunlu alan',
                                },
                              ]}
                              name={[field.name, 'areaId']}
                              fieldKey={[field.fieldKey, 'areaId']}
                              key={field.key + '_areaId'}
                              noStyle
                            >
                              <Select style={{ width: '7rem' }} loading={areasLoading}>
                                {areas?.map(m => (
                                  <Select.Option key={field.key + '_' + m.id} value={m.id}>
                                    {m.code}
                                  </Select.Option>
                                ))}
                              </Select>
                            </Form.Item>
                            <MinusCircleOutlined onClick={() => remove(field.name)} />
                          </Space>
                        </Form.Item>
                      ))}
                      <Form.Item wrapperCol={{ offset: 6 }}>
                        <Button type="dashed" onClick={() => add()} icon={<PlusOutlined />}>
                          Plan ekle
                        </Button>
                        <Form.ErrorList errors={errors} />
                      </Form.Item>
                    </>
                  )}
                </Form.List>

                <Form.Item wrapperCol={{ offset: 6, span: 18 }}>
                  <Button type="primary" htmlType="submit" loading={loading}>
                    Ekle
                  </Button>
                </Form.Item>
              </Form>
            )}
          </Spin>
        </div>
      )}
    </>
  );
}
